import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import PersonalDetailsCard from './PersonalDetailsCard';
import OtherDetailsCard from './OtherDetailsCard';
import MsgModalBox from '../../../container/MessageModalBox/MsgModalBox';
import { APIURL } from '../../../config';

const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const SectionCard = ({ icon, title, children }) => (
    <Paper
        elevation={1}
        sx={{
            flex: '1 1 0',
            minWidth: 280,
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#ffffff',
            border: '1px solid #e0e7ef',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#2c3e50' }}>
                {title}
            </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
);

const buildPersonalDetails = (props) => ({
    first_name: props.edit ? props.data.con_first_name : '',
    last_name: props.edit ? props.data.con_last_name : '',
    address_line_1: props.edit ? props.data.con_Addr_line1 : '',
    address_line_2: props.edit ? props.data.con_Addr_line2 : '',
    city: props.edit ? props.data.con_city : '',
    state: props.edit ? props.data.con_state : '',
    zip: props.edit ? props.data.con_zip : '',
    prefCommMode: props.edit ? props.data.con_email : '',
    Cemail: props.edit ? props.data.con_email : '',
    phnNumList: props.edit
        ? props.data.con_phone.split(',').map((phn, indx) => ({ phnId: indx, country_code: '+1', phone: phn.slice(2, phn.length) }))
        : [{ phnId: 1, country_code: '+1', phone: '' }],
});

const buildOtherDetails = (props) => ({
    designation: props.edit ? props.data.con_designation : '',
    customer: props.edit ? (!props.data.con_customer_id ? 'NULL' : props.data.con_customer_id) : 'NULL',
    status: props.edit ? props.data.con_status : '',
    contact_type: props.edit ? props.data.con_contact_type : '',
    first_contact_date: props.edit ? moment(props.data.con_first_contact_date).format(DATETIME_FORMAT) : moment().format(DATETIME_FORMAT),
    last_contact_date: props.edit ? moment(props.data.con_last_contact_date).format(DATETIME_FORMAT) : moment().format(DATETIME_FORMAT),
    con_note: props.edit ? props.data.con_note : '',
});

const EMPTY_PERSONAL = {
    first_name: '',
    last_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    prefCommMode: '',
    Cemail: '',
    phnNumList: [{ phnId: 1, country_code: '+1', phone: '' }],
};

const EMPTY_OTHER = {
    designation: '',
    customer: 'NULL',
    status: '',
    contact_type: '',
    first_contact_date: moment().format(DATETIME_FORMAT),
    last_contact_date: moment().format(DATETIME_FORMAT),
    con_note: '',
};

const ContactForm_v2 = (props) => {
    const [personalDetails, setPersonalDetails] = useState(() => buildPersonalDetails(props));
    const [otherDetails, setOtherDetails] = useState(() => buildOtherDetails(props));
    const [dropdowns, setDropdowns] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [addError, setAddError] = useState(false);
    const [goHome, setGoHome] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const fetchDropdowns = async () => {
            const data = {
                customerList: (await axios.get(APIURL + '/customers', { params: { company_id: props.comp_id } })).data,
                designation: (await axios.get(APIURL + '/dropdowns/dropdownType/designation', { params: { comp_id: props.comp_id } })).data,
                contactType: (await axios.get(APIURL + '/dropdowns/dropdownType/contactType', { params: { comp_id: props.comp_id } })).data,
                contactStatus: (await axios.get(APIURL + '/dropdowns/dropdownType/contactStatus', { params: { comp_id: props.comp_id } })).data,
            };
            setDropdowns(data);
            setIsLoading(false);
        };
        fetchDropdowns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.comp_id]);

    const isFormValidated = (objForVal) => {
        const result = { isValidated: true };
        for (const objKey in objForVal) {
            const value = objForVal[objKey];
            if (value === '' || value === undefined || value === null || value.length === 0) {
                if (objKey === 'address_line_2' || objKey === 'zip' || objKey === 'designation' || objKey === 'con_note' || (objKey === 'city' && objForVal.address_line_1)) {
                    continue;
                }
                result.isValidated = false;
                result.missingKey = objKey;
                return result;
            } else if (Array.isArray(value)) {
                const arrValid = value.every((objTesting) => {
                    for (const k in objTesting) {
                        if (objTesting[k] === '' || objTesting[k] === undefined) return false;
                    }
                    return true;
                });
                if (!arrValid) {
                    result.isValidated = false;
                    result.missingKey = objKey;
                    return result;
                }
            }
        }
        return result;
    };

    const validateAll = () => {
        const personalValidation = isFormValidated(personalDetails);
        const otherValidation = isFormValidated(otherDetails);
        if (!personalValidation.isValidated || !otherValidation.isValidated) {
            setErrorMsg('Please fill out all required fields highlighted below.');
            setShowAlert(true);
            return false;
        }
        setShowAlert(false);
        setErrorMsg('');
        return true;
    };

    const addContactToDB = async (e) => {
        axios.defaults.headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': '*',
            'Access-Control-Allow-Methods': 'PUT, GET, POST, DELETE, OPTIONS',
            'ACCESS-CONTROL-ALLOW-CREDENTIAL': true,
            Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token'),
        };
        e.preventDefault();
        e.persist();
        setIsSaving(true);

        const data = {
            company_id: props.comp_id,
            cont_id: !props.edit ? null : props.data.con_id,
            first_name: personalDetails.first_name,
            last_name: personalDetails.last_name,
            address_line_1: personalDetails.address_line_1,
            address_line_2: personalDetails.address_line_2,
            city: personalDetails.city,
            state: personalDetails.state,
            zip: personalDetails.zip,
            email: personalDetails.Cemail,
            prefCommMode: personalDetails.prefCommMode,
            phoneList: personalDetails.phnNumList.map((number) => number.country_code + number.phone).join(),
            designation: otherDetails.designation,
            customer_id: otherDetails.customer === 'NULL' ? null : otherDetails.customer,
            status: otherDetails.status,
            contact_type: otherDetails.contact_type,
            first_contact_date: otherDetails.first_contact_date,
            last_contact_date: otherDetails.last_contact_date,
            con_note: otherDetails.con_note,
        };

        try {
            const msg = props.edit ? await axios.put(APIURL + '/contacts', data) : await axios.post(APIURL + '/contacts', data);
            if (msg.data.code === 200) {
                e.target.reset();
                if (props.edit) {
                    props.onUpdate(personalDetails, otherDetails);
                } else if (props.fetchData) {
                    await props.fetchData();
                }
                setPersonalDetails(EMPTY_PERSONAL);
                setOtherDetails(EMPTY_OTHER);
                setSubmitAttempted(false);
                setShowModal(true);
                setAddError(false);
            } else {
                setAddError(true);
                setShowModal(true);
            }
        } catch (err) {
            setAddError(true);
            setShowModal(true);
        }
        setIsSaving(false);
    };

    const handlePersonalChange = (event) => {
        if (event.target.id === 'address_maps') {
            const components = event.value.result.address_components;
            const find = (...types) => components.find((c) => types.some((t) => c.types.includes(t)))?.long_name;
            const findShort = (...types) => components.find((c) => types.some((t) => c.types.includes(t)))?.short_name;
            setPersonalDetails((prev) => ({
                ...prev,
                address_line_1: (find('street_number') || '') + ' ' + (find('route') || ''),
                city: find('locality', 'sublocality', 'sublocality_level_1', 'neighborhood'),
                state: findShort('administrative_area_level_1'),
                zip: find('postal_code'),
            }));
            return;
        }
        const id = event.target.id;
        const value = id === 'state' ? event.target.value.toUpperCase() : event.target.value;
        setPersonalDetails((prev) => ({ ...prev, [id]: value }));
    };

    const handleOtherChange = (event) => {
        setOtherDetails((prev) => ({ ...prev, [event.target.id]: event.target.value }));
    };

    const handlePhoneListChange = (id, e) => {
        setPersonalDetails((prev) => ({
            ...prev,
            phnNumList: prev.phnNumList.map((phn) => (phn.phnId === id ? { ...phn, [e.target.id]: e.target.value } : phn)),
        }));
    };

    const onAddPhone = () => {
        setPersonalDetails((prev) => ({
            ...prev,
            phnNumList: [...prev.phnNumList, { phnId: prev.phnNumList.length + 1, country_code: '+1', phone: '' }],
        }));
    };

    const onDeletePhone = (phnId) => {
        setPersonalDetails((prev) => ({
            ...prev,
            phnNumList: prev.phnNumList.filter((phn) => phn.phnId !== phnId).map((phn, indx) => ({ ...phn, phnId: indx + 1 })),
        }));
    };

    const handleGoHome = () => {
        setAddError(false);
        setShowModal(false);
        setShowAlert(false);
        setGoHome(true);
    };

    const addNew = () => {
        setShowModal(false);
        setShowAlert(false);
        if (!addError) setAddError(false);
    };

    return (
        <Box sx={{ textAlign: 'left', px: 1 }}>
            {showModal ? (
                <MsgModalBox edit={props.edit} addNew={addNew} goHome={handleGoHome} text="Contact" showError={addError} style={{ position: 'absolute' }} />
            ) : isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box
                    component="form"
                    noValidate
                    onSubmit={(e) => {
                        setSubmitAttempted(true);
                        if (validateAll()) {
                            addContactToDB(e);
                        } else {
                            e.preventDefault();
                        }
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f3a5f', mb: 1 }}>
                        {props.edit ? 'Edit Contact' : 'New Contact'}
                    </Typography>

                    {showAlert && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMsg || 'Please fill out missing form fields'}
                        </Alert>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <SectionCard icon={<PersonOutlineOutlinedIcon sx={{ color: '#1976d2' }} />} title="Personal Details">
                            <PersonalDetailsCard
                                edit={props.edit}
                                formVals={personalDetails}
                                formChangeHandler={handlePersonalChange}
                                phnNumList={personalDetails.phnNumList}
                                addPhoneNumber={onAddPhone}
                                deletePhoneNumber={onDeletePhone}
                                handlePhoneListChange={handlePhoneListChange}
                                submitAttempted={submitAttempted}
                            />
                        </SectionCard>

                        <SectionCard icon={<ListAltOutlinedIcon sx={{ color: '#1976d2' }} />} title="Other Details">
                            <OtherDetailsCard
                                edit={props.edit}
                                formVals={otherDetails}
                                formChangeHandler={handleOtherChange}
                                designation={dropdowns.designation}
                                customerList={dropdowns.customerList}
                                contactStatus={dropdowns.contactStatus}
                                contactType={dropdowns.contactType}
                                submitAttempted={submitAttempted}
                            />
                        </SectionCard>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2 }}>
                        {props.onCancel && (
                            <Button variant="outlined" size="large" type="button" onClick={props.onCancel} disabled={isSaving}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isSaving}
                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isSaving ? (props.edit ? 'Saving...' : 'Creating...') : props.edit ? 'Save Changes' : 'Create Contact'}
                        </Button>
                    </Box>
                </Box>
            )}
            {goHome && <Redirect to="/" />}
        </Box>
    );
};

export default ContactForm_v2;
